import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, Toolbar, ResponsiveTable, StatusBadge, TableSkeleton, EmptyState, ReasonModal, DateCell, CopyId } from "@/admin/ui";
import { LogOut, Search, RefreshCw, Ban, AlertTriangle, Smartphone, Monitor, Bot, Laptop } from "lucide-react";

const DeviceIcon = ({ device }) => {
  const d = String(device || "").toLowerCase();
  if (d.startsWith("mobile") || d.startsWith("tablet")) return <Smartphone className="w-3.5 h-3.5" />;
  if (d.startsWith("bot")) return <Bot className="w-3.5 h-3.5" />;
  if (d.startsWith("desktop")) return <Laptop className="w-3.5 h-3.5" />;
  return <Monitor className="w-3.5 h-3.5" />;
};

const RiskBadge = ({ score }) => {
  const n = Number(score || 0);
  const tone = n >= 4 ? "danger" : n >= 2 ? "warning" : "neutral";
  return <StatusBadge tone={tone} testid="session-risk">risk {n}</StatusBadge>;
};

export default function AdminSessions() {
  const { t } = useLang();
  const [items, setItems] = React.useState([]);
  const [count, setCount] = React.useState(0);
  const [q, setQ] = React.useState("");
  const [activeOnly, setActiveOnly] = React.useState(true);
  const [suspiciousOnly, setSuspiciousOnly] = React.useState(false);
  const [modal, setModal] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeOnly) params.active_only = true;
      if (suspiciousOnly) params.suspicious_only = true;
      const r = await api.get("/api/admin/sessions", { params });
      setItems(r.data.items); setCount(r.data.count);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setLoading(false); }
  }, [activeOnly, suspiciousOnly]);
  React.useEffect(() => { load(); }, [load]);

  const filtered = items.filter((s) => {
    if (!q) return true;
    const n = q.toLowerCase();
    return (s.admin_email || "").toLowerCase().includes(n)
      || (s.ip || "").toLowerCase().includes(n)
      || (s.browser || "").toLowerCase().includes(n)
      || (s.os || "").toLowerCase().includes(n);
  });

  const revokeOne = (s) => setModal({
    title: t("admin.sessions.revoke_title"),
    description: `${s.admin_email} · ${s.browser} · ${s.ip || "—"}`,
    actionLabel: t("admin.sessions.revoke"), danger: true,
    requireCheck: !!s.is_current, checkLabel: t("admin.sessions.self_confirm"),
    onSubmit: async (reason) => {
      await api.post(`/api/admin/sessions/${s.id}/revoke`, { reason, confirm: !!s.is_current });
      toast.success(t("admin.sessions.revoked"));
      if (s.is_current) { window.location.href = "/login"; return; }
      await load();
    },
  });
  const forceLogoutAdmin = (s) => setModal({
    title: t("admin.sessions.force_logout_title"), description: s.admin_email,
    actionLabel: t("admin.sessions.force_logout"), danger: true,
    onSubmit: async (reason) => {
      await api.post(`/api/admin/admins/${s.admin_id}/sessions/revoke-all`, { reason });
      toast.success(t("admin.sessions.force_logout_done")); await load();
    },
  });
  const revokeOthers = () => setModal({
    title: t("admin.sessions.revoke_others_title"), description: t("admin.sessions.revoke_others_desc"),
    actionLabel: t("admin.sessions.revoke_others"),
    onSubmit: async (reason) => {
      const r = await api.post("/api/admin/sessions/revoke-others", { reason });
      toast.success(`${r.data.revoked_count} ${t("admin.sessions.revoked_plural")}`); await load();
    },
  });
  const flagSuspicious = (s) => setModal({
    title: t("admin.sessions.flag_title"), description: `${s.admin_email} · ${s.browser}`,
    actionLabel: t("admin.sessions.flag"),
    onSubmit: async (reason) => {
      await api.post(`/api/admin/sessions/${s.id}/flag-suspicious`, { reason });
      toast.success(t("admin.sessions.flagged")); await load();
    },
  });

  return (
    <div className="space-y-6" data-testid="admin-sessions-page">
      <PageHeader title={t("admin.sessions.title")} description={t("admin.sessions.subtitle")}
        primary={<button className="lp-btn-primary text-xs py-1.5 px-3" onClick={revokeOthers} data-testid="admin-sessions-revoke-others">
          <LogOut className="w-3.5 h-3.5" /> {t("admin.sessions.revoke_others")}
        </button>}
        secondary={<button className="lp-btn-ghost" onClick={load} data-testid="admin-sessions-refresh" aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>}
      />

      <Toolbar right={<div className="text-xs text-white/40">{count} {t("admin.sessions.total")}</div>}>
        <div className="flex-1 relative min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input className="lp-input w-full pl-9 py-2 text-sm" placeholder={t("admin.sessions.search_ph")}
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-sessions-search" />
        </div>
        <label className="flex items-center gap-2 text-white/70 text-xs">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} data-testid="filter-active-only" />
          {t("admin.sessions.active_only")}
        </label>
        <label className="flex items-center gap-2 text-white/70 text-xs">
          <input type="checkbox" checked={suspiciousOnly} onChange={(e) => setSuspiciousOnly(e.target.checked)} data-testid="filter-suspicious-only" />
          {t("admin.sessions.suspicious_only")}
        </label>
      </Toolbar>

      {loading ? <TableSkeleton rows={8} cols={9} /> : filtered.length === 0 ? (
        <EmptyState title={t("admin.sessions.empty")} />
      ) : (
        <ResponsiveTable minWidth={1100}>
          <thead className="lp-table-sticky"><tr>
            <th>{t("admin.sessions.col.admin")}</th>
            <th>{t("admin.sessions.col.role")}</th>
            <th>{t("admin.sessions.col.device")}</th>
            <th>IP</th>
            <th>{t("admin.sessions.col.login")}</th>
            <th>{t("admin.sessions.col.activity")}</th>
            <th>{t("admin.sessions.col.status")}</th>
            <th className="text-right">{t("admin.sessions.col.actions")}</th>
          </tr></thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} data-testid={`session-row-${s.id.slice(0, 6)}`}>
                <td>
                  <div className="font-mono text-white/90 text-xs truncate max-w-[220px]" title={s.admin_email}>{s.admin_email}</div>
                  <div className="text-white/40 text-[10px]">{s.session_id_masked}</div>
                </td>
                <td className="text-white/70 text-xs">{s.role}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <DeviceIcon device={s.device} />
                    <div>
                      <div className="text-white/85 text-xs">{s.browser}</div>
                      <div className="text-white/40 text-[10px]">{s.device} · {s.os}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="font-mono text-white/70 text-xs">{s.ip || "—"}</div>
                  {s.country && <div className="text-white/40 text-[10px]">{s.country}</div>}
                </td>
                <td><DateCell value={s.login_at} /></td>
                <td><DateCell value={s.last_activity_at} /></td>
                <td>
                  <div className="flex flex-col gap-1 items-start">
                    {s.is_current && <StatusBadge tone="success" testid="session-current-badge">{t("admin.sessions.this_device")}</StatusBadge>}
                    {s.revoked ? <StatusBadge tone="danger">revoked</StatusBadge> : <StatusBadge tone="brand">active</StatusBadge>}
                    {s.suspicious && <StatusBadge tone="warning" icon={AlertTriangle} testid="session-suspicious-badge">suspicious</StatusBadge>}
                    <RiskBadge score={s.risk_score} />
                  </div>
                </td>
                <td className="text-right">
                  <div className="inline-flex gap-1 flex-wrap justify-end">
                    {!s.revoked && (
                      <button className="lp-btn-danger text-[10px] px-2 py-1" onClick={() => revokeOne(s)} data-testid={`revoke-btn-${s.id.slice(0, 6)}`}>
                        <Ban className="w-3 h-3" /> {t("admin.sessions.revoke")}
                      </button>
                    )}
                    {!s.suspicious && !s.revoked && (
                      <button className="lp-btn-ghost" onClick={() => flagSuspicious(s)} aria-label={t("admin.sessions.flag")} data-testid={`flag-btn-${s.id.slice(0, 6)}`}>
                        <AlertTriangle className="w-3 h-3" />
                      </button>
                    )}
                    <button className="lp-btn-ghost" onClick={() => forceLogoutAdmin(s)} title={t("admin.sessions.force_logout")} aria-label={t("admin.sessions.force_logout")} data-testid={`force-logout-btn-${s.id.slice(0, 6)}`}>
                      <LogOut className="w-3 h-3" />
                    </button>
                  </div>
                  {s.revoked && (
                    <div className="text-[10px] text-white/40 mt-1">
                      by {s.revoked_by_email || "—"} · <DateCell value={s.revoked_at} />
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}

      <ReasonModal open={!!modal} {...(modal || {})} onClose={() => setModal(null)} onSubmit={modal?.onSubmit || (async () => {})} />
    </div>
  );
}
